import {
  Button,
  ButtonColor,
  ButtonState,
} from '@delmonicos/shared-design-system-react/src/components/button/button';
import { Dialog } from '@delmonicos/shared-design-system-react/src/components/dialog/dialog';
import { Icon } from '@delmonicos/shared-design-system-react/src/components/icon/icon';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { ResetType } from '@delmonicos/shared-sdk/libs/cpms/src/libs/ocpp/src/enums/reset-type.enum';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { getState } from '@app/core/helper/get-state';
import { Logger } from '@app/core/logger/logger';
import { LogCard } from '@app/feature/charge-point/charge-point-details/tabs/maintenance-tab/log-card/log-card.component';
import { ChargePointOcppTriggerMessageCreateController } from '@app/feature/charge-point/charge-point-ocpp-trigger-message-create/charge-point-ocpp-trigger-message-create.controller';
import { useChargePointContext } from '@app/feature/charge-point/charge-point.context';
import { StateEnum } from '@model/enum/state.enum';
import { useCpmsBoChargePointResetOcppMutation } from '@service/cpms-bo-charge-point-ocpp.service';

interface MaintenanceTabComponentProps {
  openDiagnosticListDialog: () => void;
}

export const MaintenanceTabComponent = ({
  openDiagnosticListDialog,
}: MaintenanceTabComponentProps): React.JSX.Element => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const chargePoint = useChargePointContext();

  const [
    actionCreateTriggerMessageIsDialogOpen,
    setActionCreateTriggerMessageIsDialogOpen,
  ] = useState(false);

  const [
    handleChargePointsResetOcppHardware,
    {
      isError: isChargePointsResetOcppHardwareError,
      isLoading: isChargePointsResetOcppHardwareLoading,
      isSuccess: isChargePointsResetOcppHardwareSuccess,
      reset: resetChargePointResetOcppHardware,
    },
  ] = useCpmsBoChargePointResetOcppMutation({
    fixedCacheKey: `/charge-points/${chargePoint.id}/ocpp/reset/hardware`,
  });

  const [
    handleChargePointsResetOcppSoftware,
    {
      isError: isChargePointsResetOcppSoftwareError,
      isLoading: isChargePointsResetOcppSoftwareLoading,
      isSuccess: isChargePointsResetOcppSoftwareSuccess,
      reset: resetChargePointResetOcppSoftware,
    },
  ] = useCpmsBoChargePointResetOcppMutation({
    fixedCacheKey: `/charge-points/${chargePoint.id}/ocpp/reset/software`,
  });

  const chargePointResetOcppHardwareState = useMemo(
    () =>
      getState({
        isLoading: isChargePointsResetOcppHardwareLoading,
        isError: isChargePointsResetOcppHardwareError,
      }),
    [
      isChargePointsResetOcppHardwareLoading,
      isChargePointsResetOcppHardwareError,
    ],
  );

  const chargePointResetOcppSoftwareState = useMemo(
    () =>
      getState({
        isLoading: isChargePointsResetOcppSoftwareLoading,
        isError: isChargePointsResetOcppSoftwareError,
      }),
    [
      isChargePointsResetOcppSoftwareLoading,
      isChargePointsResetOcppSoftwareError,
    ],
  );

  const isChargePointOcppOperationInProgress = useMemo(() => {
    if (isChargePointsResetOcppHardwareLoading) {
      return true;
    }
    if (isChargePointsResetOcppSoftwareLoading) {
      return true;
    }
    return false;
  }, [
    isChargePointsResetOcppHardwareLoading,
    isChargePointsResetOcppSoftwareLoading,
  ]);

  useEffect(() => {
    if (isChargePointsResetOcppHardwareError) {
      toast.error(t('$.charge-point-actions.reset-hardware-error'));
      resetChargePointResetOcppHardware();
    }
    if (isChargePointsResetOcppHardwareSuccess) {
      toast.success(
        t('$.charge-point-details.maintenance.reset-hardware-success'),
      );
      resetChargePointResetOcppHardware();
    }
  }, [
    isChargePointsResetOcppHardwareError,
    isChargePointsResetOcppHardwareSuccess,
    resetChargePointResetOcppHardware,
    t,
  ]);

  useEffect(() => {
    if (isChargePointsResetOcppSoftwareError) {
      toast.error(t('$.charge-point-actions.reset-software-error'));
      resetChargePointResetOcppSoftware();
    }
    if (isChargePointsResetOcppSoftwareSuccess) {
      toast.success(t('$.charge-point-actions.reset-software-success'));
      resetChargePointResetOcppSoftware();
    }
  }, [
    isChargePointsResetOcppSoftwareError,
    isChargePointsResetOcppSoftwareSuccess,
    resetChargePointResetOcppSoftware,
    t,
  ]);

  return (
    <>
      <Stack
        direction="column"
        flex={1}
        gap={GapEnum.L}
        justifyContent="flex-start">
        <Stack gap={GapEnum.XL}>
          <Stack flex={1} gap={GapEnum.L}>
            <Button
              state={
                chargePointResetOcppHardwareState === StateEnum.LOADING
                  ? ButtonState.Loading
                  : isChargePointOcppOperationInProgress
                    ? ButtonState.Disabled
                    : ButtonState.Default
              }
              color={ButtonColor.Primary}
              onClick={() =>
                handleChargePointsResetOcppHardware({
                  chargePointId: chargePoint.id,
                  type: ResetType.Hard,
                })
              }>
              <Stack
                alignItems="center"
                flex={1}
                justifyContent="space-between">
                <Icon name="refresh-cw-01" />
                <Stack flex={1} justifyContent="center">
                  {t('$.charge-point-actions.reset-hardware')}
                </Stack>
              </Stack>
            </Button>
            <Button
              state={
                chargePointResetOcppSoftwareState === StateEnum.LOADING
                  ? ButtonState.Loading
                  : isChargePointOcppOperationInProgress
                    ? ButtonState.Disabled
                    : ButtonState.Default
              }
              onClick={() =>
                handleChargePointsResetOcppSoftware({
                  chargePointId: chargePoint.id,
                  type: ResetType.Soft,
                })
              }>
              <Stack
                alignItems="center"
                flex={1}
                justifyContent="space-between">
                <Icon name="refresh-cw-01" />
                <Stack flex={1} justifyContent="center">
                  {t('$.charge-point-actions.reset-software')}
                </Stack>
              </Stack>
            </Button>
          </Stack>
          <Stack flex={1} gap={GapEnum.L}>
            <Button
              state={
                isChargePointOcppOperationInProgress
                  ? ButtonState.Disabled
                  : ButtonState.Default
              }
              color={ButtonColor.Primary}
              onClick={() => {
                openDiagnosticListDialog();
                Logger.info('Diagnostic de la borne');
              }}>
              <Stack
                alignItems="center"
                flex={1}
                justifyContent="space-between">
                <Icon name="file-search-02" />
                <Stack flex={1} justifyContent="center">
                  {t('$.charge-point-actions.diagnostic')}
                </Stack>
              </Stack>
            </Button>
            <Button
              state={
                isChargePointOcppOperationInProgress
                  ? ButtonState.Disabled
                  : ButtonState.Default
              }
              color={ButtonColor.Primary}
              onClick={() => setActionCreateTriggerMessageIsDialogOpen(true)}>
              <Stack
                alignItems="center"
                flex={1}
                justifyContent="space-between">
                <Icon name="mail-01" />
                <Stack flex={1} justifyContent="center">
                  {t('$.charge-point-ocpp-trigger-message-create.action')}
                </Stack>
              </Stack>
            </Button>
          </Stack>
        </Stack>
        {id && <LogCard chargePointId={chargePoint.id} />}
      </Stack>
      <Dialog
        open={actionCreateTriggerMessageIsDialogOpen}
        onClose={() => setActionCreateTriggerMessageIsDialogOpen(false)}>
        <Dialog.Header
          onClose={() => setActionCreateTriggerMessageIsDialogOpen(false)}
          title={t('$.charge-point-ocpp-trigger-message-create.title')}
        />
        <Dialog.Content>
          <ChargePointOcppTriggerMessageCreateController
            onCancel={() => setActionCreateTriggerMessageIsDialogOpen(false)}
            onSuccess={() => setActionCreateTriggerMessageIsDialogOpen(false)}
          />
        </Dialog.Content>
      </Dialog>
    </>
  );
};
